import React from 'react';
import { pickLocalized, formatCountdown, isInternalTallyOptionKey } from '../../utils/testUtils';
import {
  testBtnPrimary,
  testBtnSecondary,
  testBtnDisabled,
  testLink,
  testProgressFill,
  testTimerOk,
  testTimerUrgent,
} from './testUiClasses';
import AbilitySpeechBubble from './AbilitySpeechBubble';

function AbilityQuestionView({
  t,
  lang,
  paperTitle,
  sectionTitle,
  paperInstruction,
  showSectionTitle,
  usesSectionMenu,
  currentPassage,
  currentItem,
  questionIndex,
  totalQuestions,
  paperQuestionIndex,
  totalPaperQuestions,
  remainingSeconds,
  answers,
  currentAnswered,
  isLast,
  hasNextSection,
  isPenultimateSection,
  onBackFromQuestions,
  onSelectOption,
  onPrev,
  onNext,
  onFinishSection,
  onGoToNextSection
}) {
  const progressCurrent =
    typeof paperQuestionIndex === 'number' ? paperQuestionIndex + 1 : questionIndex + 1;
  const progressTotal =
    typeof totalPaperQuestions === 'number' && totalPaperQuestions > 0
      ? totalPaperQuestions
      : totalQuestions;
  const displayNumber =
    typeof currentItem.question.displayNumber === 'number'
      ? currentItem.question.displayNumber
      : progressCurrent;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex flex-col gap-3 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={onBackFromQuestions}
            className={`inline-flex items-center gap-1 ${testLink}`}
          >
            <span aria-hidden>←</span>
            {usesSectionMenu ? t('test.backToSections') : t('test.backToChapters')}
          </button>
          <div
            className={`text-sm font-mono font-semibold px-3 py-1.5 rounded-md ${
              remainingSeconds <= 300 ? testTimerUrgent : testTimerOk
            }`}
          >
            {t('test.timeRemaining')}: {formatCountdown(remainingSeconds)}
          </div>
        </div>
        <div className="text-xs text-ink-subtle">
          <span className="font-medium text-ink-muted">{paperTitle}</span>
          {sectionTitle ? (
            <>
              <span className="mx-1.5">·</span>
              <span>{sectionTitle}</span>
            </>
          ) : null}
        </div>
      </div>

      <div className="w-full bg-surface-muted rounded-full h-1.5 mb-6">
        <div
          className={`${testProgressFill} h-1.5`}
          style={{
            width: `${(progressCurrent / Math.max(progressTotal, 1)) * 100}%`
          }}
        />
      </div>

      {showSectionTitle && sectionTitle ? (
        <h2 className="text-lg font-bold text-ink mb-4 text-justify">{sectionTitle}</h2>
      ) : null}

      {currentPassage ? (
        <div className="mb-6 p-4 bg-surface-muted rounded-xl border border-edge">
          {pickLocalized(currentPassage.title, lang) ? (
            <h3 className="text-sm font-semibold text-ink mb-2 text-justify">
              {pickLocalized(currentPassage.title, lang)}
            </h3>
          ) : null}
          <p className="text-ink whitespace-pre-wrap leading-relaxed text-justify text-sm md:text-base">
            {pickLocalized(currentPassage.body, lang)}
          </p>
        </div>
      ) : null}

      <article className="bg-surface rounded-2xl border border-edge shadow-sm p-5 md:p-7 mb-6">
        <p className="text-ink-subtle text-sm font-medium mb-3">
          <span className="text-ink font-semibold">
            {t('test.questionNumberHeading', { number: displayNumber })}
          </span>
          <span className="text-ink-subtle ml-2">
            {t('test.questionProgressSequential', {
              current: progressCurrent,
              total: progressTotal
            })}
          </span>
        </p>
        <h2 className="text-lg md:text-xl font-bold text-ink font-montserrat text-justify leading-snug">
          {pickLocalized(currentItem.question.prompt, lang)}
        </h2>
        {currentItem.question.imageUrl ? (
          <div className="mt-4 rounded-lg border border-edge bg-canvas overflow-hidden">
            <img
              src={currentItem.question.imageUrl}
              alt=""
              className="w-full h-auto max-h-[min(70vh,520px)] object-contain mx-auto"
            />
          </div>
        ) : null}
      </article>

      <p className="text-xs text-ink-subtle mb-3">{t('test.optionKeysHint')}</p>

      <div className="space-y-2.5">
        {currentItem.question.options.map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => onSelectOption(currentItem.question.id, opt.key)}
            className={`w-full p-4 rounded-xl transition-colors duration-200 flex gap-3 items-start text-left ${
              String(answers[currentItem.question.id]) === String(opt.key)
                ? 'bg-accent/30 border-2 border-primary text-ink'
                : 'bg-surface-muted border border-edge text-ink hover:border-edge-strong hover:bg-surface'
            }`}
          >
            {!isInternalTallyOptionKey(opt.key) ? (
              <span className="font-semibold text-ink-subtle w-6 shrink-0 pt-0.5">{opt.key}</span>
            ) : null}
            <span className="flex-1 text-justify">{pickLocalized(opt.label, lang)}</span>
          </button>
        ))}
      </div>

      <div className="flex justify-between mt-8 gap-3">
        <button
          type="button"
          onClick={onPrev}
          disabled={questionIndex === 0}
          className={`text-sm ${
            questionIndex === 0 ? testBtnDisabled : testBtnSecondary
          }`}
        >
          {t('test.previousQuestion')}
        </button>
        {isLast ? (
          hasNextSection ? (
            <div className="relative">
              {isPenultimateSection && usesSectionMenu ? (
                <AbilitySpeechBubble placement="top" align="end">
                  {t('test.nextSectionNudge')}
                </AbilitySpeechBubble>
              ) : null}
              <button
                type="button"
                onClick={onGoToNextSection}
                disabled={!currentAnswered}
                aria-describedby={isPenultimateSection && usesSectionMenu ? 'ability-next-section-nudge' : undefined}
                className={!currentAnswered ? testBtnDisabled : testBtnPrimary}
              >
                {t('test.nextSection')}
              </button>
              {isPenultimateSection && usesSectionMenu ? (
                <span id="ability-next-section-nudge" className="sr-only">
                  {t('test.nextSectionNudge')}
                </span>
              ) : null}
            </div>
          ) : (
            <button
              type="button"
              onClick={onFinishSection}
              disabled={!currentAnswered}
              className={!currentAnswered ? testBtnDisabled : testBtnPrimary}
            >
              {usesSectionMenu ? t('test.finishSection') : t('test.submitTest')}
            </button>
          )
        ) : (
          <button
            type="button"
            onClick={onNext}
            disabled={!currentAnswered}
            className={!currentAnswered ? testBtnDisabled : testBtnPrimary}
          >
            {t('test.nextQuestion')}
          </button>
        )}
      </div>

      {paperInstruction ? (
        <p className="mt-8 text-xs text-ink-subtle text-justify border-t border-edge pt-4">
          {paperInstruction}
        </p>
      ) : null}
    </div>
  );
}

export default AbilityQuestionView;
